import { mix } from "polished";
import React, { ComponentPropsWithoutRef } from "react";
import { IconType } from "react-icons";
import { FaTimes } from "react-icons/fa";
import styled, { DefaultTheme, useTheme } from "styled-components";
import { space, SpaceProps } from "styled-system";
import { IconButton } from "..";
import { accessibleShade } from "../accessibleColor";

export type LabelProps = SpaceProps & {
  size?: "small" | "normal" | "tiny";
  /** A theme colour name (e.g. "danger") or any CSS colour. */
  color?: string;
  /** Text colour. Setting it implies the solid variant. */
  textColor?: string;
  /**
   * soft (default): a light tint of the colour with deep text.
   * solid: the colour itself with white text, for rare strong emphasis.
   */
  variant?: "soft" | "solid";
  icon?: IconType;
  onRemove?: () => void;
  children?: React.ReactNode;
} & ComponentPropsWithoutRef<"span">;

/**
 * Aliases onto the theme's hand-tuned soft/text pairs. A colour whose own
 * `<name>Soft` and `<name>Text` exist needs no entry here — see `softPair`.
 */
const SOFT_TOKENS: Record<string, string> = {
  orange: "warning",
  green: "positive",
  blue: "info",
};

export type LabelColors = { bg: string; text: string };
type Colors = LabelColors;

/** Works out the label's background and text so the text passes AA. */
export function labelColors(
  theme: DefaultTheme,
  color: string | undefined,
  textColor: string | undefined,
  variant: "soft" | "solid",
): Colors {
  const colors = theme.colors;

  if (color == null && textColor == null) {
    // Neutral label.
    return { bg: colors.grey6, text: colors.grey1 };
  }

  const base = (color && (colors[color] ?? color)) || colors.grey2;

  if (variant === "solid" || textColor != null) {
    const text = textColor ?? "#fff";
    return { bg: accessibleShade(base, text), text };
  }

  // The theme's own pair wins, then an alias. Without this, a colour that
  // only exists as a Soft/Text pair (e.g. "feedback", "goals") falls through
  // to mix(), which throws on a name polished can't parse, and the label
  // ends up with no background and white text.
  const token = color != null ? (SOFT_TOKENS[color] ?? color) : undefined;
  if (token && colors[`${token}Soft`] && colors[`${token}Text`]) {
    return { bg: colors[`${token}Soft`], text: colors[`${token}Text`] };
  }

  try {
    const bg = mix(0.14, base, "#fff");
    return { bg, text: accessibleShade(base, bg) };
  } catch {
    // Gradients and other values polished can't parse.
    return { bg: base, text: "#fff" };
  }
}

const LabelElem = styled.span<{
  size: "small" | "normal" | "tiny";
  colors: Colors;
}>`
  background-color: ${(props) => props.colors.bg};
  color: ${(props) => props.colors.text};
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  padding: ${(props) =>
    props.size === "small"
      ? "3px 8px"
      : props.size === "tiny"
        ? "2px 7px"
        : "5px 12px"};
  border-radius: 999px;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 5px;

  & + & {
    margin-left: 4px;
  }

  ${space};
`;

const Label = ({
  color,
  textColor,
  variant = "soft",
  size = "normal",
  icon: Icon,
  onRemove,
  children,
  ...rest
}: LabelProps) => {
  const theme = useTheme();
  const colors = labelColors(theme, color, textColor, variant);

  return (
    <LabelElem {...rest} size={size} colors={colors}>
      {Icon && <Icon aria-hidden="true" />}
      {children}
      {onRemove && (
        <IconButton
          ml={1}
          icon={FaTimes}
          onClick={onRemove}
          color={colors.text}
          aria-label="Remove"
        />
      )}
    </LabelElem>
  );
};

export default Label;
